import React, { useState, useEffect } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TextField, Box, Stack } from '@mui/material';
import { format, parse, isValid } from 'date-fns';
import { es } from 'date-fns/locale'; 
import { esES } from '@mui/x-date-pickers/locales';

const DateRangeFormField = ({ 
  onDateRangeChange, 
  value = {}, 
  required = false,
  error = false,
  helperText = "",
  disabled = false,
  minDate = null
}) => {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [localError, setLocalError] = useState('');

  const parseDate = (value) => {
    if (!value) return null;
    const parsedDate = parse(value, 'dd/MM/yyyy', new Date());
    return isValid(parsedDate) ? parsedDate : null;
  };

  const handleStartDateChange = (date) => {
    let newDate = date;
    
    if (typeof date === 'string') {
      newDate = parseDate(date);
    }
    
    setStartDate(newDate);
    
    if (endDate && newDate > endDate) {
      setLocalError('La fecha de inicio no puede ser mayor que la fecha final');
    } else {
      setLocalError('');
      if (newDate || endDate) {
        onDateRangeChange({
          startDate: newDate ? format(newDate, 'yyyy-MM-dd') : '',
          endDate: endDate ? format(endDate, 'yyyy-MM-dd') : ''
        });
      }
    }
  };

  const handleEndDateChange = (date) => {
    let newDate = date;
    
    if (typeof date === 'string') {
      newDate = parseDate(date);
    }
    
    setEndDate(newDate);
    
    if (startDate && newDate < startDate) {
      setLocalError('La fecha final no puede ser menor que la fecha de inicio');
    } else {
      setLocalError('');
      if (startDate || newDate) {
        onDateRangeChange({
          startDate: startDate ? format(startDate, 'yyyy-MM-dd') : '',
          endDate: newDate ? format(newDate, 'yyyy-MM-dd') : ''
        });
      }
    }
  };

  const sharedDateFieldSx = {
  '& .MuiOutlinedInput-root': {
    height: 40,
    borderRadius: 2,
    color: '#333',
    '& fieldset': {
      border: '1px solid #D1D5DB !important',
    },
    '&:hover fieldset': {
      border: '1px solid #D1D5DB !important',
    },
    '&.Mui-focused fieldset': {
      border: '1px solid #D1D5DB !important',
      boxShadow: 'none',
      outline: 'none',
    },
    },
  '& .MuiInputLabel-root': {
    fontSize: '0.875rem',
    '&.Mui-focused': {
      color: '#000',
    },
  },
  '& .MuiInputBase-input': {
    padding: '10px 14px',
    fontSize: '0.875rem',
    color: '#000',
  },
  };

  useEffect(() => {
    if (value.startDate) {
      setStartDate(parse(value.startDate, 'yyyy-MM-dd', new Date()));
    } else {
      setStartDate(null);
    }
    
    if (value.endDate) {
      setEndDate(parse(value.endDate, 'yyyy-MM-dd', new Date()));
    } else {
      setEndDate(null);
    }
  }, [value.startDate, value.endDate]);

  const displayError = error || localError;
  const displayHelperText = helperText || localError;

  return (
    <LocalizationProvider 
      dateAdapter={AdapterDateFns} 
      adapterLocale={es} 
      localeText={esES.components.MuiLocalizationProvider.defaultProps.localeText}
    >
      <Box sx={{ mt: 0.5 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <DatePicker
            label={`Inicio`}
            value={startDate}
            onChange={handleStartDateChange}
            maxDate={endDate || undefined}
            minDate={minDate || undefined}
            inputFormat="dd/MM/yyyy"
            mask="__/__/____"
            required={required}
            disabled={disabled}
            slotProps={{
              textField: {
                fullWidth: true,
                size: 'small',
                required,
                color: 'inherit', 
                sx: sharedDateFieldSx,
                error: !!displayError,
                helperText: displayError ? displayHelperText : '',
              },
            }}
          />

          <DatePicker
            label={`Fin`}
            value={endDate}
            onChange={handleEndDateChange}
            minDate={startDate || undefined}
            inputFormat="dd/MM/yyyy"
            mask="__/__/____"
            required={required}
            disabled={disabled}
            slotProps={{
              textField: {
                fullWidth: true,
                size: 'small',
                required,
                color: 'inherit',
                sx: sharedDateFieldSx,
                error: !!displayError,
                helperText: displayError ? displayHelperText : '',
              },
            }}
          />
        </Stack>
        
        {displayError && (
          <Box sx={{ color: 'error.main', fontSize: '0.75rem', mt: 1 }}>
            {displayHelperText}
          </Box>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default DateRangeFormField;